import { useMemo } from "react";
import { SortDescriptor } from "react-aria-components";

import { StackRoleBindingView } from "shared/Stack/Roles/types";

import { STACK_ROLES_COLUMN_ID } from "./constants";

const sortRoles = (
  roles: StackRoleBindingView[],
  sortDescriptor: SortDescriptor
): StackRoleBindingView[] => {
  // If no sort column or direction is provided, return unsorted copy
  if (!sortDescriptor.column || !sortDescriptor.direction) {
    return [...roles];
  }

  const sortedRoles = [...roles];

  return sortedRoles.sort((a, b) => {
    let aValue, bValue;

    const columnId = sortDescriptor.column as STACK_ROLES_COLUMN_ID;

    switch (columnId) {
      case STACK_ROLES_COLUMN_ID.ROLE:
        aValue = a.role?.name;
        bValue = b.role?.name;
        break;
      case STACK_ROLES_COLUMN_ID.SPACE:
        aValue = a.space.name;
        bValue = b.space.name;
        break;
      case STACK_ROLES_COLUMN_ID.DESCRIPTION:
      case STACK_ROLES_COLUMN_ID.ROW_ACTIONS:
      default:
        return 0; // No sorting for non-sortable columns
    }

    // null/undefined values should always come last
    if (!aValue && !bValue) {
      return 0; // Both null, consider equal
    }
    if (!aValue) {
      return 1; // a is null, should be placed after b
    }
    if (!bValue) {
      return -1; // b is null, should be placed after a
    }

    // Sort based on direction
    if (sortDescriptor.direction === "ascending") {
      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue);
      }
      return aValue > bValue ? 1 : -1;
    } else {
      if (typeof aValue === "string" && typeof bValue === "string") {
        return bValue.localeCompare(aValue);
      }
      return bValue > aValue ? 1 : -1;
    }
  });
};

export const useFilteredRoles = (roles: StackRoleBindingView[], sortDescriptor: SortDescriptor) => {
  return useMemo(() => {
    let filteredRoles = [...roles];

    // Apply sorting if descriptor exists
    if (sortDescriptor.column) {
      filteredRoles = sortRoles(filteredRoles, sortDescriptor);
    }

    return filteredRoles;
  }, [roles, sortDescriptor]);
};
